import { useEffect, useState, useMemo } from "react";
import { Button, Cascader, Modal, Radio, Select, Tag, Tooltip } from "antd";
import { Plus } from "lucide-react";
import {
  get_standard_blocks,
  get_operation_type,
  get_process_operation_by_recipe,
} from "../mbmr/mbmr_api";
import { get_api_function } from '@/utils/api';
import {
  setPrevOperationId,
  setSelectedOperationType,
  setSelectedStandardBlock,
} from "./slice/processConfig";
import { dispatch } from '@/store';
import { useSelector } from "react-redux";
import { useTheme } from '@/App';
import { getAccentColor } from '@/themes/antd-theme';
import { useParams } from "react-router";

const { OptGroup } = Select;
export default function OperationButton({
  onSubmit,
  isModalOpen = false,
  setIsModalOpen,
  // Current recipe's packaging flag (from the process-config page, via
  // get_process_operation_by_recipe). Sent as ?is_packaging=true|false to
  // filter operation types + copy-source processes.
  recipeIsPackaging = false,
  // The process this operation is being added to, if it has a Process Type
  // with an "operations usable" whitelist configured — narrows the Operation
  // Type dropdown to that whitelist (unrestricted when null or unconfigured).
  processTypeId = null,
}) {
  const { recipe_id } = useParams();
  const processConfig = useSelector((state) => state.processConfig);
  const { isDark } = useTheme();
  const { theme_color } = useSelector((state) => state.company_profile);
  const buttonBgColor = getAccentColor(theme_color, isDark);

  const [choice, setChoice] = useState("custom"); // "standard" or "custom"
  const [selectedBlock, setSelectedBlock] = useState("");
  const [selectedOperationType, setOperationType] = useState("");
  const [standard_block_list, set_standard_block_list] = useState([]);
  const [operation_type_list, set_operation_type_list] = useState([]);
  const [previousOperationOptions, setPreviousOperationOptions] = useState([]);
  const [selectedPreviousOperation, setSelectedPreviousOperation] = useState(null);
  const [selectedCopyOperationId, setSelectedCopyOperationId] = useState(null);
  const [loadingProcesses, setLoadingProcesses] = useState(false);
  // 2026-10-29 (owner) · Copy Operation: record → process → operation, from the records of this record's product
  const [copySources, setCopySources] = useState([]);
  const [copyPath, setCopyPath] = useState([]);

  const success_get_standard_blocks = (res) => {
    if (res.data.status === "success") {
      set_standard_block_list(res.data.data);
    }
  };

  const success_get_operation_type = (res) => {
    if (res.data.status === "success") {
      set_operation_type_list(res.data.data);
    }
  };

  // Group operations by category
  const groupedOperations = useMemo(() => {
    const groups = {};
    operation_type_list.forEach((op) => {
      const category = op.category || "General Operations";
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(op);
    });
    return groups;
  }, [operation_type_list]);

  useEffect(() => {
    if (isModalOpen) {
      // This is a single-select picker (not a paginated table), so ask the
      // backend for the full unpaginated list instead of its default 15-per-page.
      get_standard_blocks(success_get_standard_blocks, { all: true });
      if (recipe_id) {
        get_process_operation_by_recipe((res) => {
          if (res?.data?.status !== "success") {
            setPreviousOperationOptions([]);
            // Fall back to the prop's packaging flag if the recipe fetch failed.
            get_operation_type(success_get_operation_type, { is_packaging: !!recipeIsPackaging, process_type_id: processTypeId || undefined });
            return;
          }
          // Recipe's packaging flag straight from THIS response — authoritative,
          // no state/prop timing race. Fetch op-types with it immediately so the
          // op-type dropdown is always filtered correctly (packaging vs mfg).
          const pkg = res?.data?.is_packaging !== undefined
            ? !!res.data.is_packaging
            : !!recipeIsPackaging;
          get_operation_type(success_get_operation_type, { is_packaging: pkg, process_type_id: processTypeId || undefined });

          const processList = res?.data?.data || [];
          const ops = processList
            .flatMap((proc) =>
              (proc?.operation || []).map((op) => ({
                operation_id: op?.operation_id,
                operation_number: op?.operation_number,
                operation_name: op?.operation || op?.operation_name || op?.operation_type || "",
                process_name: proc?.process_name || "",
              }))
            )
            .sort((a, b) => Number(a.operation_number) - Number(b.operation_number));
          setPreviousOperationOptions(ops);

          const preferred = processConfig?.previous_operation_id;
          if (preferred && ops.some((op) => String(op.operation_id) === String(preferred))) {
            setSelectedPreviousOperation(preferred);
          } else {
            setSelectedPreviousOperation(ops.length > 0 ? ops[ops.length - 1]?.operation_id : null);
          }
        }, { recipe_id });
      } else {
        // No recipe (library process): use the prop's flag for the op-type filter.
        setPreviousOperationOptions([]);
        setSelectedPreviousOperation(null);
        get_operation_type(success_get_operation_type, { is_packaging: !!recipeIsPackaging, process_type_id: processTypeId || undefined });
      }
    }
    setSelectedBlock("");
    setChoice("custom");
    setSelectedCopyOperationId(null);
    setCopyPath([]);
  }, [isModalOpen, recipe_id, processConfig?.previous_operation_id, processTypeId]);

  // 2026-10-28 (owner) · the Operation Library is the Standard Block list (Recipe Building Blocks →
  // Standard Blocks), in the record designer as on /process-config. It also offered every operation
  // type designed with steps (2026-10-07), which made it longer than the Standard Blocks page.
  const libraryEntries = standard_block_list || [];

  return (
    <div title="Add Operation By">
      {/* Modal */}
      <Modal
        title="Add Operation By"
        open={isModalOpen}
        width={600}
        onCancel={() => {
          setSelectedBlock("");
          setChoice("custom");
          setSelectedCopyOperationId(null);
          setCopyPath([]);
          setIsModalOpen(false);
          dispatch(setSelectedOperationType(null));
          dispatch(setSelectedStandardBlock(null));
          dispatch(setPrevOperationId(null));
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              dispatch(setPrevOperationId(null));
              setIsModalOpen(false);
              setSelectedBlock("");
              dispatch(setSelectedOperationType(null));
              dispatch(setSelectedStandardBlock(null));
              setSelectedPreviousOperation(null);
              setChoice("custom");
              setSelectedCopyOperationId(null);
              setCopyPath([]);
            }}
          >
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            className="text-white"
            style={{ backgroundColor: buttonBgColor }}
            onClick={() => {
              if (choice === "standard" && !selectedBlock) {
                Modal.warning({ title: "Please select an operation library" });
                return;
              }
              if (choice === "custom" && !selectedOperationType) {
                Modal.warning({ title: "Please select an operation type" });
                return;
              }
              if (choice === "copy" && !selectedCopyOperationId) {
                Modal.warning({ title: "Please select an operation to copy" });
                return;
              }
              const selectedAfterOp = previousOperationOptions.find(
                (op) => String(op.operation_id) === String(selectedPreviousOperation)
              );
              if (previousOperationOptions.length > 0 && !selectedAfterOp) {
                Modal.warning({ title: "Please select 'Add After Operation'" });
                return;
              }

              // If next operation number is already occupied, backend will shift.
              const nextOpNumber = selectedAfterOp ? Number(selectedAfterOp.operation_number) + 1 : null;
              const isNextOccupied =
                nextOpNumber != null &&
                previousOperationOptions.some((op) => Number(op.operation_number) === nextOpNumber);
              if (isNextOccupied) {
                console.info(
                  `[AddOperation] Next operation number ${nextOpNumber} is occupied; backend will shift operation numbers.`
                );
              }

              onSubmit({
                type: choice,
                block: selectedBlock,
                operation_type: selectedOperationType,
                previous_operation_id:
                  selectedAfterOp?.operation_number || null,
                copy_from_operation_id:
                  choice === "copy" ? selectedCopyOperationId : null,
              });
              setIsModalOpen(false);
              setSelectedBlock("");
              setChoice("custom");
              setOperationType("");
              setSelectedPreviousOperation(null);
              setSelectedCopyOperationId(null);
              setCopyPath([]);
            }}
          >
            Submit
          </Button>,
        ]}
      >
        {/* Radio Buttons with Label */}
        <div className="mb-4">
          {/* <label className="block font-medium text-gray-700 dark:text-slate-200 mb-1">
            Choose Operation Type
          </label> */}
          <Radio.Group
            onChange={(e) => {
              const next = e.target.value;
              if (next === "custom") {
                setSelectedBlock("");
                setSelectedCopyOperationId(null);
                setCopyPath([]);
              } else if (next === "copy") {
                setSelectedBlock("");
                setOperationType("");
                setSelectedCopyOperationId(null);
                setCopyPath([]);
                setLoadingProcesses(true);
                // 2026-10-29 (owner) · the copy source is every record of this record's product (this one included):
                // record → process → operation (backend process_copy_sources.operation_sources).
                setCopyPath([]);
                get_api_function(`/designer/recipes/${recipe_id}/operation-copy-sources/`, (res) => {
                  setLoadingProcesses(false);
                  setCopySources(res?.data?.status === "success" ? (res?.data?.data || []) : []);
                }, {});
              } else {
                setOperationType("");
                setSelectedCopyOperationId(null);
                setCopyPath([]);
              }
              setChoice(next);
            }}
            value={choice}
            className="flex flex-col gap-2"
            aria-label="Operation Type"
          >
            <Radio value="standard">Operation Library</Radio>
            <Radio value="custom">Custom Operation</Radio>
            <Radio value="copy">Copy Operation</Radio>
          </Radio.Group>
        </div>

        {/* Dropdown if Operation Library */}
        {choice === "standard" ? (
          <div className="mt-3">
            <label
              htmlFor="standard-block-select"
              className="block font-medium text-gray-700 dark:text-slate-200 mb-1"
            >
              Select Operation Library
            </label>
            <Select
              showSearch
              id="standard-block-select"
              placeholder="Select an operation library"
              value={selectedBlock}
              onChange={setSelectedBlock}
              style={{ width: "100%" }}
              aria-label="Operation Library"
              optionFilterProp="label"
              filterOption={(input, option) =>
                option?.label?.toLowerCase().includes(input.toLowerCase())
              }
            >
              {libraryEntries.map((block, index) => (
                <Select.Option
                  key={`${block.operation_type_id}-${index}`}
                  value={block?.operation_type_id}
                  label={block?.operation_type} // 👈 add this
                >
                  {block?.operation_type}
                </Select.Option>
              ))}
            </Select>
          </div>
        ) : (
          choice === "custom" && (
            <div className="mt-3">
              <label
                htmlFor="standard-block-select"
                className="block font-medium text-gray-700 dark:text-slate-200 mb-1"
              >
                Select Operation Type
              </label>
              <Select
                showSearch
                id="operation-type-select"
                placeholder="Select operation type"
                value={selectedOperationType}
                onChange={setOperationType}
                style={{ width: "100%" }}
                aria-label="Operation Type"
                optionFilterProp="label"
                filterOption={(input, option) => {
                  const searchValue = input.toLowerCase();
                  return (
                    option?.label?.toLowerCase().includes(searchValue) ||
                    option?.code?.toLowerCase().includes(searchValue)
                  );
                }}
              >
                {Object.entries(groupedOperations).map(([category, operations]) => (
                  <OptGroup key={category} label={category}>
                    {operations.map((op) => (
                      <Select.Option
                        key={op.operation_type_id}
                        value={op.operation_type_id}
                        label={op.operation_type}
                        code={op.code}
                      >
                        <Tooltip
                          title={
                            <div>
                              <div className="font-semibold mb-1">{op.description}</div>
                              {op.isa88_phase_type && (
                                <div className="mt-2 text-xs">
                                  <span className="font-semibold">ISA-88 Phase:</span> {op.isa88_phase_type}
                                </div>
                              )}
                              {op.isa88_equipment_class && (
                                <div className="text-xs">
                                  <span className="font-semibold">Equipment Class:</span> {op.isa88_equipment_class}
                                </div>
                              )}
                            </div>
                          }
                          placement="right"
                        >
                          <div className="flex items-center justify-between">
                            <span>
                              {op.code && (
                                <Tag className="mr-2 text-xs">{op.code}</Tag>
                              )}
                              {op.isa88_phase_type && (
                                <Tag color="purple" className="mr-2 text-xs">{op.isa88_phase_type}</Tag>
                              )}
                              {op.operation_type}
                            </span>
                            {op.is_critical && (
                              <Tag color="red" className="ml-2 text-xs">
                                CRITICAL
                              </Tag>
                            )}
                          </div>
                        </Tooltip>
                      </Select.Option>
                    ))}
                  </OptGroup>
                ))}
              </Select>
            </div>
          )
        )}

        {choice === "copy" ? (
          <div className="mt-3">
            <label className="block font-medium text-gray-700 dark:text-slate-200 mb-1">
              Select Operation To Copy
            </label>
            <Cascader
              style={{ width: "100%" }}
              value={copyPath}
              placeholder="Select the MBMR, its process, then the operation"
              allowClear
              expandTrigger="hover"
              loading={loadingProcesses}
              showSearch={{ filter: (input, path) => path.some((o) => String(o.label).toLowerCase().includes(input.toLowerCase())) }}
              options={copySources.map((r) => ({
                value: `r${r.recipe_id}`,
                label: `${r.name} · v${r.version}${r.code ? ` (${r.code})` : ""}${r.current ? " — this record" : ""}`,
                children: r.processes.map((pr) => ({
                  value: `p${pr.process_id}`,
                  label: pr.process_name,
                  children: pr.operations.map((op) => ({
                    value: op.operation_id,
                    label: `Op ${op.number}${op.name ? ` - ${op.name}` : ""}`,
                  })),
                })),
              }))}
              onChange={(path) => {
                setCopyPath(path || []);
                setSelectedCopyOperationId((path && path[2]) || null);
              }}
              notFoundContent="No record of this product has an operation to copy"
            />
          </div>
        ) : null}

        <div className="mt-3">
          <label className="block font-medium text-gray-700 dark:text-slate-200 mb-1">
            Add After Operation
          </label>
          <Select
            showSearch
            placeholder="Select previous operation"
            value={selectedPreviousOperation}
            onChange={(value) => setSelectedPreviousOperation(value || null)}
            style={{ width: "100%" }}
            allowClear
            optionFilterProp="label"
            options={previousOperationOptions.map((op) => ({
              value: op.operation_id,
              label: `Op ${op.operation_number}${op.operation_name ? ` - ${op.operation_name}` : ""}${op.process_name ? ` (${op.process_name})` : ""}`,
            }))}
            notFoundContent="No operations available in current recipe"
          />
        </div>
      </Modal>
    </div>
  );
}
